// inv/src/components/ui/pagination.jsx
import { memo, useMemo, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

const BTN_SLOT = 40;

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  locked = false,
  useUrl = false,
  queryKey = "p",
  replaceHistory = true,
  leftWindow = 4,
  rightWindow = 5,
  className = "",
}) => {
  const navigate = useNavigate();
  const shouldHide = !totalPages || totalPages <= 1;
  const containerRef = useRef(null);
  const [maxSlots, setMaxSlots] = useState(Infinity);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      const width = entry?.contentRect?.width ?? node.offsetWidth ?? 0;
      const slots = Math.max(3, Math.floor((width - 4) / BTN_SLOT));
      setMaxSlots(slots);
    });
    ro.observe(node);
    return () => ro.disconnect();
  }, []);

  const pageNumbers = useMemo(() => {
    const pages = [];
    let startPage = currentPage - leftWindow;
    let endPage = currentPage + rightWindow;

    if (startPage < 1) {
      startPage = 1;
      endPage = Math.min(totalPages, 1 + leftWindow + rightWindow);
    } else if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, totalPages - (leftWindow + rightWindow));
    }

    for (let i = startPage; i <= endPage; i++) pages.push(i);
    return pages;
  }, [currentPage, totalPages, leftWindow, rightWindow]);

  const pushUrl = useCallback(
    (page) => {
      if (!useUrl) return;
      const search = new URLSearchParams(window.location.search);
      search.set(queryKey, String(page));
      navigate({ search: `?${search.toString()}` }, { replace: replaceHistory });
    },
    [navigate, useUrl, queryKey, replaceHistory]
  );

  const handlePageChange = useCallback(
    (page) => {
      if (locked) return;
      if (page < 1 || page > totalPages || page === currentPage) return;
      onPageChange(page);
      pushUrl(page);
    },
    [currentPage, totalPages, onPageChange, pushUrl, locked]
  );

  const goToPrevPage = useCallback(
    () => handlePageChange(Math.max(1, currentPage - 1)),
    [currentPage, handlePageChange]
  );
  const goToNextPage = useCallback(
    () => handlePageChange(Math.min(totalPages, currentPage + 1)),
    [currentPage, totalPages, handlePageChange]
  );

  const items = useMemo(() => {
    const build = (winLeft, winRight, showEdges = true) => {
      const arr = [];

      arr.push({
        key: "prev",
        type: "button",
        label: "‹",
        aria: "Previous page",
        onClick: goToPrevPage,
        disabled: currentPage === 1,
      });

      let startPage = Math.max(1, currentPage - winLeft);
      let endPage = Math.min(totalPages, currentPage + winRight);

      const needsLeftEllipsis = showEdges && startPage > 2;
      const needsLeftFirst = showEdges && startPage > 1;

      if (needsLeftFirst) {
        arr.push({ key: 1, type: "page", page: 1, isActive: currentPage === 1 });
      }
      if (needsLeftEllipsis) {
        arr.push({ key: "ellipsisl", type: "ellipsis" });
      }

      for (let p = startPage; p <= endPage; p++) {
        arr.push({ key: p, type: "page", page: p, isActive: currentPage === p });
      }

      const needsRightEllipsis = showEdges && endPage < totalPages - 1;
      const needsRightLast = showEdges && endPage < totalPages;

      if (needsRightEllipsis) {
        arr.push({ key: "ellipsiss", type: "ellipsis" });
      }
      if (needsRightLast) {
        arr.push({
          key: totalPages,
          type: "page",
          page: totalPages,
          isActive: currentPage === totalPages,
        });
      }

      arr.push({
        key: "next",
        type: "button",
        label: "›",
        aria: "Next page",
        onClick: goToNextPage,
        disabled: currentPage === totalPages,
      });

      return arr;
    };

    let wl = leftWindow;
    let wr = rightWindow;
    let showEdges = true;

    let arr = build(wl, wr, showEdges);

    while (arr.length > maxSlots && (wl > 0 || wr > 0)) {
      if (wr >= wl && wr > 0) wr--;
      else if (wl > 0) wl--;
      arr = build(wl, wr, showEdges);
    }

    if (arr.length > maxSlots && showEdges) {
      showEdges = false;
      arr = build(wl, wr, showEdges);
    }

    if (arr.length > maxSlots) {
      return [
        {
          key: "prev",
          type: "button",
          label: "‹",
          aria: "Previous page",
          onClick: goToPrevPage,
          disabled: currentPage === 1,
        },
        { key: "curr", type: "page", page: currentPage, isActive: true },
        {
          key: "next",
          type: "button",
          label: "›",
          aria: "Next page",
          onClick: goToNextPage,
          disabled: currentPage === totalPages,
        },
      ];
    }

    return arr;
  }, [currentPage, totalPages, leftWindow, rightWindow, goToPrevPage, goToNextPage, maxSlots]);

  const baseBtn =
    "mr-1 rounded-md relative inline-flex items-center justify-center px-2 py-1 text-sm select-none " +
    "border border-stone-200 text-stone-950 " +
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400/70 " +
    "disabled:opacity-50 disabled:cursor-not-allowed " +
    "min-w-8";

  const clickable = "cursor-pointer";
  const activeBtn = "bg-stone-200 text-stone-950 hover:bg-stone-300 focus-visible:ring-stone-700 ";

  if (shouldHide) return null;

  return (
    <nav
      ref={containerRef}
      className={`pagination flex ${className}`}
      role="navigation"
      aria-label="Pagination"
      style={{ overflow: "hidden" }}
    >
      <div className="inline-flex whitespace-nowrap">
        {items.map((it, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === items.length - 1;
          const joinGlue = !isFirst ? "-ml-px" : "";
          const radius = (isFirst ? "rounded-l-md " : "") + (isLast ? "rounded-r-md " : "");

          if (it.type === "ellipsis") {
            return (
              <span
                key={it.key}
                className={[baseBtn, joinGlue, "cursor-default pointer-events-none text-stone-400"].join(" ")}
                aria-hidden="true"
              >
                …
              </span>
            );
          }

          if (it.type === "page") {
            const isActive = !!it.isActive;
            return (
              <button
                key={it.key}
                type="button"
                onClick={() => handlePageChange(it.page)}
                disabled={locked}
                aria-label={`Page ${it.page}`}
                aria-current={isActive ? "page" : undefined}
                className={[baseBtn, clickable, joinGlue, radius, isActive ? activeBtn : ""].join(" ")}
              >
                {it.page}
              </button>
            );
          }

          return (
            <button
              key={it.key}
              type="button"
              onClick={it.onClick}
              disabled={it.disabled || locked}
              aria-label={it.aria}
              className={[baseBtn, clickable, joinGlue, radius].join(" ")}
            >
              {it.label}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  useUrl: PropTypes.bool,
  queryKey: PropTypes.string,
  replaceHistory: PropTypes.bool,
  leftWindow: PropTypes.number,
  rightWindow: PropTypes.number,
  className: PropTypes.string,
};

export default memo(Pagination);
